<template>
  <section
    class="relative w-full overflow-hidden py-4 tablet:pb-20 tablet:pt-12"
    :class="bannerClass"
    style="
      background-image: linear-gradient(
        0.31deg,
        var(--tw-gradient-to) 50%,
        var(--tw-gradient-from) 99.73%
      );
    "
  >
    <div class="container-wrap">
      <p
        class="mb-6 truncate text-sm tablet:text-base tablet:leading-7"
        :class="breadcrumbClass"
      >
        <span
          v-for="item in breadcrumbs"
          :key="item"
          class="after:mx-1 after:text-gray-600 after:content-['/'] last:after:content-['']"
        >
          {{ item }}
        </span>
      </p>
      <div
        class="mb-9 tablet:mb-0 tablet:grid tablet:grid-cols-2 tablet:gap-4 desktop:gap-6"
      >
        <div class="flex flex-col justify-center">
          <h1
            class="text-h1 mb-6 line-clamp-1 text-center tablet:mb-5 tablet:text-left"
          >
            {{ title }}
          </h1>
          <TheRichText
            v-if="!isMobile && (descriptionHtml || description)"
            class="mb-5 line-clamp-5 text-base text-gray-700 desktop:text-lg"
            :text="descriptionHtml || description"
            :is-html="!!descriptionHtml"
          />
          <div v-if="!isMobile" class="flex items-center pt-5">
            <TheButton
              v-if="button01"
              :id="button01.trackingCode"
              :to="button01.link"
              :title="button01.text"
              class="group mr-2.5 inline-flex items-center desktop:mr-6"
              @click="handleClickButton(0)"
            >
              <span class="font-bold">
                {{ button01.text }}
              </span>
              <IconArrow
                direction="right"
                class="ml-3 hidden group-hover:block"
              />
            </TheButton>
            <TheButton
              v-if="button02"
              :id="button02.trackingCode"
              :to="button02.link"
              :title="button02.text"
              class="mr-2.5 desktop:mr-6"
              type="outline"
              @click="handleClickButton(1)"
            >
              <span class="font-bold">
                {{ button02.text }}
              </span>
            </TheButton>
          </div>
        </div>
        <div class="flex items-center">
          <TheMedia
            :title="title"
            :description="description"
            :video-data="wistiaCode ? video : undefined"
            :thumbnail="thumbnail"
            :theme="theme"
            class="aspect-video w-full rounded-3xl"
          />
        </div>
      </div>
      <div v-if="isMobile">
        <TheButton
          v-if="button01"
          :id="button01.trackingCode"
          :to="button01.link"
          :title="button01.text"
          class="mb-4 inline-flex w-full items-center justify-center"
          @click="handleClickButton(0)"
        >
          <span class="font-bold">
            {{ button01.text }}
          </span>
          <IconArrow direction="right" class="ml-1" />
        </TheButton>

        <TheButton
          v-if="button02"
          :id="button02.trackingCode"
          :to="button02.link"
          :title="button02.text"
          type="outline"
          class="inline-flex w-full items-center justify-center"
          @click="handleClickButton(1)"
        >
          <span class="font-bold">
            {{ button02.text }}
          </span>
        </TheButton>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
  /**
   * @see https://www.figma.com/design/QJ3ZvEbUHI9xI11iudYf8U/Advantech-WISE-Library?node-id=12796-23844&t=89PIFDiQEC4zcSbf-0
   */

  import type { IVideoData } from '@/components/Shared/Lightbox/Video.vue';
  import TheMedia from '@/components/Shared/TheMedia.vue';

  interface IButton {
    text: string;
    link: string;
    trackingCode?: string;
  }

  interface IProps {
    breadcrumbs: string[];
    title: string;
    description: string;
    descriptionHtml?: string;
    button01?: IButton;
    button02?: IButton;
    wistiaCode?: string;
    thumbnail: string;
    theme?: TTheme;
  }

  const props = withDefaults(defineProps<IProps>(), {
    wistiaCode: undefined,
    descriptionHtml: undefined,
    button01: undefined,
    button02: undefined,
    theme: 'blue',
  });

  const emit = defineEmits<{
    (e: 'click-button', index: number): void;
  }>();

  const { isMobile } = useScreen();

  const bannerClass = computed((): string => {
    switch (props.theme) {
      case 'green':
        return 'from-green-150 to-white';
      case 'cyan':
        return 'from-cyan-150 to-white';
      case 'purple':
        return 'from-purple-150 to-white';
      case 'yellow':
        return 'from-yellow-150 to-white';
      default:
        return 'from-blue-150 to-white';
    }
  });

  const breadcrumbClass = computed((): string => {
    switch (props.theme) {
      case 'green':
        return 'text-green-400';
      case 'cyan':
        return 'text-cyan-400';
      case 'purple':
        return 'text-purple-400';
      case 'yellow':
        return 'text-yellow-500';
      default:
        return 'text-blue-400';
    }
  });

  const video = computed(
    (): IVideoData => ({
      title: props.title,
      description: props.description,
      url: props.wistiaCode || '',
      imageUrl: props.thumbnail,
    })
  );

  const handleClickButton = (index: number): void => {
    emit('click-button', index);
  };

  export type { IButton };
</script>
